// Shared notification visual helpers: icons, date formatting, item card.
// V4-T14 : gestion des notifications orphelines (cible supprimée)
// V5-T13 : bouton dismiss au hover
import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  AtSign,
  ListChecks,
  Ban,
  Clock,
  CheckCircle2,
  MessageCircle,
  XCircle,
  CalendarClock,
  CalendarCheck,
  CalendarX,
  UserCheck,
  UserPlus,
  Bell,
  Receipt,
  Package,
  Hourglass,
  FileWarning,
  Wallet,
  Send,
  X,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

/**
 * Format a date as a relative time string in French (e.g. "il y a 5 minutes").
 * @param {string|Date} date
 * @returns {string}
 */
export function relative(date) {
  if (!date) return "";
  return formatDistanceToNow(new Date(date), { locale: fr, addSuffix: true });
}

// Les trois types mémo ci-dessous étaient émis par le backend mais absents de
// cette table : ils s'affichaient avec la cloche générique et, surtout,
// n'apparaissaient pas dans les préférences de notification (ALL_TYPES), donc
// personne ne pouvait s'en désabonner.
export const TYPE_LABELS = {
  "memo.urgent":          "Mémo urgent",
  "memo.mentioned":       "Mention dans un mémo",
  "memo.assigned":        "Mémo assigné",
  "memo.commented":       "Commentaire sur un mémo",
  "task.assigned":        "Tâche assignée",
  "task.blocked":         "Tâche bloquée",
  "task.due_soon":        "Tâche bientôt échue",
  "timesheet.submitted":  "Feuille d'heures soumise",
  "timesheet.approved":   "Feuille d'heures approuvée",
  "timesheet.rejected":   "Feuille d'heures rejetée",
  "absence.submitted":    "Demande d'absence",
  "absence.approved":     "Absence approuvée",
  "absence.rejected":     "Absence rejetée",
  "assignment.created":   "Nouvelle assignation",
  "maintenance.plan_due_soon": "Maintenance préventive à échéance",
  "presence.no_show":     "Non pointé (alerte matinale)",
  "memo.urgent_reminder": "Relance mémo urgent",
  // V6 — miroir de NOTIFICATION_TYPES (backend/models/notifications.py) :
  // un type absent d'ici s'affiche avec la cloche générique et disparaît des
  // préférences, donc personne ne peut s'en désabonner.
  "expense.submitted":    "Note de frais soumise",
  "expense.approved":     "Note de frais approuvée",
  "expense.rejected":     "Note de frais refusée",
  "stock.low_threshold":  "Stock sous le seuil",
  "task.review_overdue":  "Validation de tâche en attente",
  "site.schedule_drift":  "Dérive de délai chantier",
  "absence.proof_due":            "Justificatif d'arrêt attendu",
  "subcontractor.invoice_due_soon": "Facture ST à payer bientôt",
  "punch.forgot_clock_out":       "Pointage resté ouvert",
  "timesheet.missing":            "Feuille d'heures manquante",
  "task.predecessor_done":        "Prérequis de tâche levé",
  // Émis par tasks_workflow.py sans figurer dans aucun des deux
  // registres : cloche générique à l'affichage, et pas de case à
  // cocher dans les préférences pour le couper.
  "task.submitted":               "Tâche soumise à validation",
};

export const ALL_TYPES = Object.keys(TYPE_LABELS);

const ICONS = {
  "memo.urgent":         { Icon: AlertTriangle,  color: "text-red-600 bg-red-50" },
  "memo.mentioned":      { Icon: AtSign,         color: "text-violet-600 bg-violet-50" },
  "memo.assigned":       { Icon: UserCheck,      color: "text-violet-600 bg-violet-50" },
  "memo.commented":      { Icon: MessageCircle,  color: "text-slate-600 bg-slate-100" },
  "task.assigned":       { Icon: ListChecks,     color: "text-blue-600 bg-blue-50" },
  "task.blocked":        { Icon: Ban,            color: "text-orange-600 bg-orange-50" },
  "task.due_soon":       { Icon: Clock,          color: "text-amber-600 bg-amber-50" },
  "timesheet.submitted": { Icon: Clock,          color: "text-indigo-600 bg-indigo-50" },
  "timesheet.approved":  { Icon: CheckCircle2,   color: "text-emerald-600 bg-emerald-50" },
  "timesheet.rejected":  { Icon: XCircle,        color: "text-red-600 bg-red-50" },
  "absence.submitted":   { Icon: CalendarClock,  color: "text-indigo-600 bg-indigo-50" },
  "absence.approved":    { Icon: CalendarCheck,  color: "text-emerald-600 bg-emerald-50" },
  "absence.rejected":    { Icon: CalendarX,      color: "text-red-600 bg-red-50" },
  "assignment.created":  { Icon: UserPlus,       color: "text-blue-600 bg-blue-50" },
  "maintenance.plan_due_soon": { Icon: Clock,     color: "text-teal-600 bg-teal-50" },
  "presence.no_show":    { Icon: AlertTriangle,   color: "text-amber-600 bg-amber-50" },
  "memo.urgent_reminder": { Icon: AlertTriangle,  color: "text-red-600 bg-red-50" },
  "expense.submitted":   { Icon: Receipt,        color: "text-indigo-600 bg-indigo-50" },
  "expense.approved":    { Icon: CheckCircle2,   color: "text-emerald-600 bg-emerald-50" },
  "expense.rejected":    { Icon: XCircle,        color: "text-red-600 bg-red-50" },
  "stock.low_threshold": { Icon: Package,        color: "text-amber-600 bg-amber-50" },
  "task.review_overdue": { Icon: Hourglass,      color: "text-orange-600 bg-orange-50" },
  "site.schedule_drift": { Icon: CalendarClock,  color: "text-rose-600 bg-rose-50" },
  "absence.proof_due":   { Icon: FileWarning,    color: "text-amber-600 bg-amber-50" },
  "subcontractor.invoice_due_soon": { Icon: Wallet, color: "text-indigo-600 bg-indigo-50" },
  "punch.forgot_clock_out": { Icon: Clock,       color: "text-orange-600 bg-orange-50" },
  "timesheet.missing":   { Icon: FileWarning,    color: "text-orange-600 bg-orange-50" },
  "task.predecessor_done": { Icon: ListChecks,   color: "text-emerald-600 bg-emerald-50" },
  "task.submitted":      { Icon: Send,           color: "text-indigo-600 bg-indigo-50" },
};

export function NotificationIcon({ type }) {
  const entry = ICONS[type] || { Icon: Bell, color: "text-muted-foreground bg-muted" };
  const { Icon, color } = entry;
  return (
    <span className={`h-9 w-9 rounded-full grid place-items-center ${color}`}>
      <Icon className="h-4 w-4" />
    </span>
  );
}

export function isOrphaned(notif) {
  if (!notif.link) return false;
  const { link, type } = notif;
  if (type?.startsWith("task.") && link?.includes("/tasks/")) return false;
  if (type?.startsWith("absence.") && link?.includes("/absences")) return false;
  if (type?.startsWith("timesheet.") && link?.includes("/timesheets")) return false;
  return false;
}

/**
 * NotificationItem
 * Props :
 *   notif     : notification document
 *   onClick   : (notif) => void   — clic principal (marquer lu + nav)
 *   onDismiss : (notif) => void   — V5-T13, optionnel
 *   compact   : bool              — mode popover (sans bordure extérieure)
 */
export default function NotificationItem({ notif, onClick, onDismiss, compact = false }) {
  const isRead = !!notif.read_at;

  const content = (
    <div
      data-testid="notification-item"
      className={`
        group relative flex items-start gap-3 px-3 py-3 cursor-pointer select-none
        transition-colors rounded-lg
        ${isRead ? "opacity-70" : ""}
        ${compact ? "hover:bg-muted" : "hover:bg-muted/80"}
      `}
      onClick={() => onClick?.(notif)}
    >
      {/* Point bleu non-lu */}
      {!isRead && (
        <span
          className="absolute left-1 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary"
          aria-label="Non lu"
        />
      )}

      <NotificationIcon type={notif.type} />

      <div className="min-w-0 flex-1">
        <p className={`text-sm leading-snug ${isRead ? "font-normal text-muted-foreground" : "font-semibold text-foreground"}`}>
          {notif.title}
        </p>
        {notif.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.body}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          {notif.created_at
            ? formatDistanceToNow(new Date(notif.created_at), { locale: fr, addSuffix: true })
            : ""}
        </p>
      </div>

      {/* Bouton dismiss (V5-T13) — visible au hover */}
      {onDismiss && (
        <button
          data-testid="dismiss-notif-btn"
          className="
            absolute right-2 top-2
            opacity-0 group-hover:opacity-100
            transition-opacity
            h-6 w-6 rounded-full
            flex items-center justify-center
            text-muted-foreground hover:text-foreground hover:bg-gray-200
          "
          onClick={(e) => { e.stopPropagation(); onDismiss(notif); }}
          title="Masquer cette notification"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  if (notif.link && !isOrphaned(notif)) {
    return <Link to={notif.link} className="block">{content}</Link>;
  }
  return content;
}
